"use client";

import { IconArrowNarrowRight } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { Rating } from "@/components/common/rating";
import { ListingItemCateringImage } from "@/components/listing/listing-item-catering-image";
import { Badge } from "@/components/ui/badge";
import type { CateringCatalog } from "@/features/caterings/types/catering-catalog.type";
import { getLocaleForPathname, Link } from "@/i18n/navigation";
import { useMobile } from "@/lib/hooks/use-mobile";
import { cn } from "@/lib/utils";

type NoResultsOfferCateringProps = {
  className?: string;
  catering: CateringCatalog;
};

export const NoResultsOfferCatering = ({
  className,
  catering,
}: NoResultsOfferCateringProps) => {
  const t = useTranslations("diets");
  const locale = getLocaleForPathname();
  const { isMobile } = useMobile();

  return (
    <div className="rounded-lg bg-brand-green2-300 p-4 text-white">
      <div
        className={cn(
          "flex w-full items-center gap-4 max-md:items-start",
          className,
        )}
      >
        <ListingItemCateringImage
          className="h-20 w-20 shrink-0"
          imageUrl={catering.imageUrl}
          name={catering.name}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-2xl">{catering.name}</p>
            {!isMobile && <NoResultsOfferCateringBadges catering={catering} />}
          </div>
          <p className="text-white max-md:text-sm">
            {t("no_results_offer.catering_subtitle")}
          </p>
        </div>
        {catering.slug && (
          <Link
            className="mt-auto mb-3 flex shrink-0 items-center gap-1 font-medium text-base underline underline-offset-3 max-md:mt-1 max-md:hidden md:ml-auto"
            href={{
              pathname: "/caterings/[slug]",
              params: { slug: catering.slug },
            }}
            locale={locale}
            title={catering.name}
          >
            {t("no_results_offer.cta")}
            <IconArrowNarrowRight className="ml-2 h-4 w-4" />
          </Link>
        )}
      </div>
      {isMobile && (
        <div className="mt-4">
          <NoResultsOfferCateringBadges catering={catering} />
        </div>
      )}
    </div>
  );
};

const NoResultsOfferCateringBadges = ({
  catering,
}: {
  catering: CateringCatalog;
}) => {
  const t = useTranslations("diets");
  return (
    <>
      <Rating
        count={catering.rating.count}
        details={false}
        isTrending={catering.rating.isTrending}
        productId={catering.id}
        productName={catering.name}
        rating={catering.rating.value}
      />
      {/* {catering.isPromoted && ( */}
      <Badge
        variant="default"
        className="rounded-full bg-white py-1.5 font-medium text-brand-green2-300 text-xs"
      >
        {t("badges.promoted")}
      </Badge>
      {/* )} */}
    </>
  );
};
